// ─────────────────────────────────────────────────────────────────
// Glass-i — Payment / Receipt Voucher grid columns (Stage 6B.6).
// ─────────────────────────────────────────────────────────────────

import { Tag } from 'antd';
import { formatINR } from '../../../../components/common/erp-grid';
import {
  PAYMENT_TYPE_LABEL, PAYMENT_TYPE_COLOR,
  PAYMENT_MODE_LABEL,
  PAYMENT_STATUS_COLOR, PAYMENT_STATUS_LABEL,
  PARTY_TYPE_LABEL,
  // Stage 6B.7C — cheque tracking labels / colours.
  CHEQUE_STATUS_LABEL, CHEQUE_STATUS_COLOR
} from './paymentVoucherConstants';

function fmtDate(v) {
  return v ? String(v).slice(0, 10) : <span className="erp-grid__muted">—</span>;
}
function fmtTs(v) {
  if (!v) return <span className="erp-grid__muted">—</span>;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return <span>{d.toISOString().slice(0, 10)} {d.toISOString().slice(11, 16)}</span>;
}

export function buildPaymentColumns({ currentPage, pageSize } = {}) {
  return [
    { title: '#', key: '__sno__', width: 56, align: 'center', fixed: 'left',
      render: (_v, _r, idx) => {
        const cp = Number(currentPage) > 0 ? Number(currentPage) : 1;
        const ps = Number(pageSize)    > 0 ? Number(pageSize)    : 25;
        return <span className="erp-grid__muted">{(cp - 1) * ps + idx + 1}</span>;
      } },
    { title: 'Voucher No', dataIndex: 'paymentNo', key: 'paymentNo',
      sortField: 'payment_no', sorter: true,
      width: 160, fixed: 'left',
      render: (v) => v
        ? <code className="erp-grid__code">{v}</code>
        : <span className="erp-grid__muted">—</span> },
    { title: 'Date', dataIndex: 'paymentDate', key: 'paymentDate',
      sortField: 'payment_date', sorter: true,
      width: 110, dataType: 'date', render: fmtDate },
    { title: 'Type', dataIndex: 'paymentType', key: 'paymentType',
      sortField: 'payment_type', width: 110, dataType: 'enum',
      render: (v) => (
        <Tag color={PAYMENT_TYPE_COLOR[v] || 'default'} className="erp-grid__tag">
          {PAYMENT_TYPE_LABEL[v] || v}
        </Tag>
      ) },
    { title: 'Party', dataIndex: 'partyName', key: 'party',
      render: (_v, r) => (
        r.partyName ? (
          <span>
            <code style={{ fontSize: 11, color: '#888', marginRight: 6 }}>
              {r.partyCode}
            </code>
            {r.partyName}
            <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>
              · {PARTY_TYPE_LABEL[r.partyType] || r.partyType}
            </span>
          </span>
        ) : <span className="erp-grid__muted">—</span>
      ) },
    { title: 'Mode', dataIndex: 'mode', key: 'mode',
      sortField: 'mode', width: 110, dataType: 'enum',
      render: (v) => <Tag>{PAYMENT_MODE_LABEL[v] || v}</Tag> },
    { title: 'Amount (₹)', dataIndex: 'amount', key: 'amount',
      sortField: 'amount', sorter: true,
      width: 140, align: 'right',
      render: (v) => {
        const n = Number(v) || 0;
        return n === 0 ? <span className="erp-grid__muted">—</span> : formatINR(n);
      } },
    { title: 'Status', dataIndex: 'status', key: 'status',
      sortField: 'status', sorter: true,
      width: 100, align: 'center',
      dataType: 'enum',
      groupable: true, groupField: 'status',
      filters: [
        { text: 'Draft',     value: 'draft' },
        { text: 'Posted',    value: 'posted' },
        { text: 'Cancelled', value: 'cancelled' }
      ],
      filterField: 'status',
      render: (v) => (
        <Tag color={PAYMENT_STATUS_COLOR[v] || 'default'} className="erp-grid__tag"
             style={{ marginInlineEnd: 0 }}>
          {(PAYMENT_STATUS_LABEL[v] || v || '').toUpperCase()}
        </Tag>
      ) },
    { title: 'Posted At', dataIndex: 'postedAt', key: 'postedAt',
      sortField: 'posted_at', sorter: true,
      width: 150, dataType: 'date', render: fmtTs },
    // Stage 6B.7C — cheque tracking columns, hidden by default.
    // Operators can show them via the grid's column picker when they
    // need to review pending / bounced cheques.
    { title: 'Cheque No', dataIndex: 'chequeNo', key: 'chequeNo',
      sortField: 'cheque_no', width: 130, dataType: 'string', hidden: true,
      render: (v) => v
        ? <code className="erp-grid__code">{v}</code>
        : <span className="erp-grid__muted">—</span> },
    { title: 'Cheque Status', dataIndex: 'chequeStatus', key: 'chequeStatus',
      sortField: 'cheque_status', width: 130, dataType: 'enum', hidden: true,
      filters: [
        { text: 'Pending',   value: 'pending'   },
        { text: 'Deposited', value: 'deposited' },
        { text: 'Cleared',   value: 'cleared'   },
        { text: 'Bounced',   value: 'bounced'   },
        { text: 'Cancelled', value: 'cancelled' }
      ],
      filterField: 'cheque_status',
      render: (v) => v
        ? <Tag color={CHEQUE_STATUS_COLOR[v] || 'default'} className="erp-grid__tag">
            {(CHEQUE_STATUS_LABEL[v] || v || '').toUpperCase()}
          </Tag>
        : <span className="erp-grid__muted">—</span> }
  ];
}
